export type NotifyChangesResult = { sent: boolean; count: number };

export type NotifyConfirmation = { text: string; tone: 'success' | 'muted' };

/**
 * The inline confirmation shown beside the "Notify vendor of changes" button
 * after a publish, derived from what the API actually did.
 *
 * `result` is null until the admin has clicked Notify in this page session.
 *
 * The two branches guard on `pendingChangesCount` differently, on purpose:
 *
 * - A success tick is suppressed once changes are pending again, so a stale
 *   "sent" never sits next to a live Notify button.
 * - The no-email notice must survive a non-zero count. `sent: false` is reached
 *   when the round's verdicts were reverted to pending; those rows still carry
 *   draft_reviewed_at, so countPendingVendorChanges keeps counting them and the
 *   button stays visible. Guarding it the same way would hide the message in
 *   the only situation it exists for.
 */
export function notifyConfirmation(
  result: NotifyChangesResult | null,
  pendingChangesCount: number,
): NotifyConfirmation | null {
  if (result === null) return null;
  if (!result.sent) return { text: 'No email sent', tone: 'muted' };
  if (pendingChangesCount > 0) return null;
  return { text: 'Changes sent ✓', tone: 'success' };
}
